import { Icon } from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import {
  mdiAlertCircleOutline,
  mdiAlertOutline,
  mdiCheck,
  mdiInformationOutline,
} from "@mdi/js";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getListAnnouncementsQueryKey,
  useListAnnouncements,
} from "../../../__generated__/orval/endpoints/announcements/announcements";
import { EntitiesAnnouncementCollectionListResponse } from "../../../__generated__/orval/models/entitiesAnnouncementCollectionListResponse.zod";
import type { EntitiesAnnouncementCollectionMain as Announcement } from "../../../__generated__/orval/models/entitiesAnnouncementCollectionMain.zod";
import type { ListAnnouncementsParams } from "../../../__generated__/orval/models/listAnnouncementsParams.zod";
import { useAuth } from "../../../hooks/useAuth";

const selectAnnouncements = (
  response: EntitiesAnnouncementCollectionListResponse,
) => {
  const result = EntitiesAnnouncementCollectionListResponse.safeParse(response);
  if (!result.success) {
    throw result.error;
  }
  return result.data.results;
};

const announcementModalIcon = {
  error: mdiAlertCircleOutline,
  info: mdiInformationOutline,
  success: mdiCheck,
  warning: mdiAlertOutline,
};

const AnnouncementAlert: React.FC<{ announcement: Announcement }> = ({
  announcement,
}) => {
  const [open, setOpen] = useState(true);

  return (
    <Collapse in={open}>
      <Alert
        icon={
          <Icon
            fontSize="inherit"
            icon={announcementModalIcon[announcement.severity]}
          />
        }
        onClose={() => setOpen(false)}
        severity={announcement.severity}
        sx={{ flexGrow: 1, mb: 2 }}
      >
        <AlertTitle>{announcement.title}</AlertTitle>
        {/** biome-ignore lint/security/noDangerouslySetInnerHtml: The announcement content is sanitized in back-end admin and safe to use */}
        <Box dangerouslySetInnerHTML={{ __html: announcement.content }} />
      </Alert>
    </Collapse>
  );
};

export const AnnouncementBanner: React.FC = () => {
  const { loading, user } = useAuth();
  const { i18n } = useTranslation();
  const [params] = useState<ListAnnouncementsParams>(() => {
    const now = new Date().toISOString();
    return {
      active: true,
      display_type: ["banner"],
      "ends_at[gte]": now,
      "starts_at[lte]": now,
    };
  });
  const { data: announcements = [], error } = useListAnnouncements(params, {
    query: {
      enabled: !!user && !loading,
      queryKey: [
        ...getListAnnouncementsQueryKey(params),
        user?.id,
        i18n.resolvedLanguage,
      ],
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      retry: false,
      select: selectAnnouncements,
    },
  });

  useEffect(() => {
    if (error) {
      Sentry.captureException(error);
    }
  }, [error]);

  if (!user || loading || error || announcements.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 2, mx: 2 }}>
      {announcements.map((announcement) => (
        <AnnouncementAlert announcement={announcement} key={announcement.id} />
      ))}
    </Box>
  );
};
